<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import type { VueCssClass } from '@@/types'
import type { Virtualizer, VirtualItem } from '@tanstack/vue-virtual'
import { useVirtualizer } from '@tanstack/vue-virtual'
import { computed, shallowRef, watch } from 'vue'
import type { CSSProperties } from 'vue'

const props = withDefaults(
  defineProps<{
    /**
     * The direction of the virtual list.
     * Can be `vertical` or `horizontal`.
     */
    direction: 'vertical' | 'horizontal'
    /**
     * The items to be rendered in the virtual list.
     * TODO: When upgrade to Vue 3, make this component generic and use `T[]` instead of `any[]`.
     */
    items: any[]
    /**
     * Function to estimate the size of each item.
     * @param index - The index of the item in the list.
     * @returns The estimated size of the item in pixels.
     */
    estimateItemSize: (index: number) => number
    /**
     * The number of items to render outside the visible area for better performance.
     * @default 5
     */
    overscan?: number
    /**
     * The gap between items in pixels.
     */
    itemGap?: number
    /**
     * Function to get a unique key for each item.
     * @param index - The index of the item in the list.
     * @returns A unique key for the item
     * @default Using Tanstack Vue Virtual's default, which is the index of the item.
     */
    getItemKey?: (index: number) => string | number
  }>(),
  {
    overscan: 5,
    itemGap: undefined,
    getItemKey: undefined,
  },
)

const emit = defineEmits<{
  (event: 'scroll', scrollOffset: number): void
}>()

// #region Setting up the virtualizer
const rootEl = shallowRef<HTMLElement>()

const virtualizerOptions = computed(() => ({
  isRtl: dir() === 'rtl',
  horizontal: props.direction === 'horizontal',
  count: props.items.length,
  getScrollElement: () => rootEl.value ?? null,
  estimateSize: props.estimateItemSize,
  overscan: props.overscan,
  gap: props.itemGap,
  getItemKey: props.getItemKey,
}))

const virtualizer = useVirtualizer<HTMLElement, HTMLElement>(virtualizerOptions)

const virtualItems = computed(() => virtualizer.value.getVirtualItems())
const totalSize = computed(() => virtualizer.value.getTotalSize())
const scrollOffset = computed(() => virtualizer.value.scrollOffset ?? 0)
// #endregion

// #region Virtualizer styles
const virtualContainerSize = computed<CSSProperties>(() => {
  return props.direction === 'vertical'
    ? {
        height: `${totalSize.value}px`,
        width: '100%',
      }
    : {
        width: `${totalSize.value}px`,
        height: '100%',
      }
})

const getVirtualItemStyles = (item: VirtualItem): CSSProperties => {
  return props.direction === 'vertical'
    ? {
        height: `${item.size}px`,
        transform: `translateY(${item.start}px)`,
      }
    : {
        width: `${item.size}px`,
        transform: `translateX(${item.start}px)`,
      }
}
// #endregion

// #region Exposing functionality
watch(scrollOffset, (newOffset) => {
  emit('scroll', newOffset)
})

const scrollToIndex: Virtualizer<HTMLElement, HTMLElement>['scrollToIndex'] = (index, options) => {
  virtualizer.value.scrollToIndex(index, options)
}

// NOTE: Never expose `virtualizer` directly.
// - If it's a value you want to expose, create a computed property and expose it.
// - If it's a method, create a wrapper function and expose it.
//
// This makes using them much easier, for example:
//
// const virtualList = shallowRef<InstanceType<typeof OzVirtualList>>()
// virtualList.value.scrollToIndex(5)
//
// Instead of:
// virtualList.value.virtualizer.scrollToIndex(5)
defineExpose({
  virtualItems,
  scrollOffset,
  scrollToIndex,
})
// #endregion
</script>

<template>
  <!-- The scrollable element for your list -->
  <div
    ref="rootEl"
    :class="[
      {
        'overflow-x-auto': direction === 'horizontal',
        'overflow-y-auto': direction === 'vertical',
      },
    ]"
  >
    <!-- The large inner element to hold all of the items -->
    <div class="relative" :style="virtualContainerSize">
      <!-- Only the visible items in the virtualizer, manually positioned to be in view -->
      <div
        v-for="item in virtualItems"
        :key="`${item.key}`"
        :class="[
          'absolute',
          'top-0 start-0',
          {
            'w-full': direction === 'vertical',
            'h-full': direction === 'horizontal',
          },
        ]"
        :style="getVirtualItemStyles(item)"
      >
        <slot name="item" :item="item" :data="items[item.index]" />
      </div>
    </div>

    <slot name="endAdornment" />
  </div>
</template>
